/**
 * Utility functions for handling departments with icons from backend
 */

/**
 * Get emoji icon for a department from backend data
 * @param {string} departmentIcon - Icon from backend database
 * @returns {string} Emoji icon for the department
 */
export const getDepartmentIcon = (departmentIcon) => {
  // Use icon from backend - it's already stored in the database
  return departmentIcon || '🏢';
};

/**
 * Format department name with icon from backend
 * @param {string} departmentName - Name of the department
 * @param {string} departmentIcon - Icon from backend database
 * @returns {string} Formatted department name with icon
 */
export const formatDepartmentWithIcon = (departmentName, departmentIcon) => {
  if (!departmentName) return 'N/A';
  const icon = getDepartmentIcon(departmentIcon);
  return `${icon} ${departmentName}`;
};

/**
 * Navbar/table display for user department assignments.
 * 1 dept → name with icon; 2+ → "Multiple Departments" with full list in tooltip.
 * @param {Array<{name: string, icon?: string}>} departments
 * @returns {{ label: string, tooltip: string|null, sortKey: string }}
 */
export const formatDepartmentsDisplay = (departments = []) => {
  if (!departments || departments.length === 0) {
    return { label: '-', tooltip: null, sortKey: '' };
  }
  if (departments.length === 1) {
    const dept = departments[0];
    return {
      label: formatDepartmentWithIcon(dept.name, dept.icon),
      tooltip: null,
      sortKey: (dept.name || '').toLowerCase(),
    };
  }
  const tooltip = departments
    .map((dept) => formatDepartmentWithIcon(dept.name, dept.icon))
    .join(', ');
  return {
    label: formatDepartmentWithIcon('Multiple Departments', '🏢'),
    tooltip,
    sortKey: 'multiple departments',
  };
};

/**
 * Get department icon only
 * @param {string} departmentIcon - Icon from backend database
 * @returns {string} Just the emoji icon
 */
export const getDepartmentIconOnly = (departmentIcon) => {
  return getDepartmentIcon(departmentIcon);
};

export default {
  getDepartmentIcon,
  formatDepartmentWithIcon,
  formatDepartmentsDisplay,
  getDepartmentIconOnly
};
